import React, { useState, useEffect, useRef } from 'react'
import { X, Gift, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import usePaymentStore from '../store/usePaymentStore'

/**
 * PromoCodeModal — модальное окно для ввода и активации промокода
 * Использует usePaymentStore для активации и обработки ошибок
 */
const PromoCodeModal = () => {
  const { t } = useTranslation()
  const {
    promoInputOpen,
    isActivatingPromo,
    promoActivateError,
    setPromoInputOpen,
    activatePromoCode,
    clearPromoError
  } = usePaymentStore()

  const [code, setCode] = useState('')
  const inputRef = useRef(null)

  // Фокус на поле ввода при открытии модалки
  useEffect(() => {
    if (promoInputOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100)
    }
  }, [promoInputOpen])

  // Сброс поля и ошибки при закрытии
  useEffect(() => {
    if (!promoInputOpen) {
      setCode('')
      clearPromoError()
    }
  }, [promoInputOpen, clearPromoError])

  // Закрытие по Escape
  useEffect(() => {
    if (!promoInputOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isActivatingPromo) {
        setPromoInputOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [promoInputOpen, isActivatingPromo, setPromoInputOpen])

  // Если модалка закрыта — не рендерим
  if (!promoInputOpen) return null

  /**
   * Обработка активации промокода
   */
  const handleActivate = async () => {
    const trimmedCode = code.trim()
    if (!trimmedCode || isActivatingPromo) return

    const success = await activatePromoCode(trimmedCode)
    if (success) {
      // При успехе закрываем модалку
      setPromoInputOpen(false)
    }
  }

  /**
   * Получить текст ошибки активации по коду ошибки бэкенда
   */
  const getErrorText = (error) => {
    if (!error) return null

    // Маппинг ошибок бэкенда на ключи локализации
    const errorMap = {
      'promo not found': 'promo.errors.notFound',
      'promo expired': 'promo.errors.expired',
      'promo already activated': 'promo.errors.alreadyActivated',
      'promo limit reached': 'promo.errors.limitReached',
      'error backend': 'promo.errors.backend'
    }

    const key = errorMap[error] || 'promo.errors.backend'
    return t(key)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
      onClick={() => !isActivatingPromo && setPromoInputOpen(false)}
    >
      <div
        className="bg-surface rounded-2xl border border-white border-opacity-10 p-6 w-full max-w-sm mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Кнопка закрытия */}
        <button
          onClick={() => !isActivatingPromo && setPromoInputOpen(false)}
          className="absolute top-4 end-4 text-text-secondary hover:text-white transition-colors"
          disabled={isActivatingPromo}
        >
          <X size={20} />
        </button>

        {/* Иконка и заголовок */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-xl flex items-center justify-center mb-3">
            <Gift size={28} className="text-dark-primary" />
          </div>
          <h2 className="text-xl font-bold text-white text-center">
            {t('promo.enterCode')}
          </h2>
        </div>

        {/* Поле ввода промокода */}
        <div className="mb-4">
          <input
            ref={inputRef}
            type="text"
            dir="ltr"
            value={code}
            onChange={(e) => {
              setCode(e.target.value)
              if (promoActivateError) clearPromoError()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleActivate()
            }}
            placeholder={t('promo.placeholder')}
            disabled={isActivatingPromo}
            className="w-full px-4 py-3 bg-dark-primary rounded-lg border border-white border-opacity-10 text-white text-lg font-semibold text-center uppercase tracking-wide focus:outline-none focus:border-accent-primary transition-colors disabled:opacity-50"
            maxLength={32}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        {/* Сообщение об ошибке */}
        {promoActivateError && (
          <div className="mb-4 bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm text-center">
              {getErrorText(promoActivateError)}
            </p>
          </div>
        )}

        {/* Кнопка активации */}
        <button
          onClick={handleActivate}
          disabled={!code.trim() || isActivatingPromo}
          className={`w-full py-3 rounded-lg font-bold text-base transition-all ${
            code.trim() && !isActivatingPromo
              ? 'bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary hover:opacity-90'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isActivatingPromo ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={18} className="animate-spin" />
              {t('promo.activating')}
            </span>
          ) : (
            t('promo.activate')
          )}
        </button>
      </div>
    </div>
  )
}

export default PromoCodeModal
